var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import React from 'react';
import { autorun } from 'mobx';
import * as monaco from 'monaco-editor';
import cx from 'classnames';
import MonacoEditor from 'react-monaco-editor';
import { observer } from 'mobx-react';
import gcLangService from '@greycat/lang-monaco';
import LanguageIcon from '../icons/LanguageIcon';
import EditorStore from '../../stores/EditorStore';
import Pane from '../pane/Pane';
import styles from './editor-pane.module.scss';
let EditorPane = class EditorPane extends React.Component {
    constructor(props) {
        super(props);
        this._editor = null;
        this._updateModelDisposer = null;
        this._lintModelWhenExecuted = null;
        this._interpretTimeout = null;
        this.onEditorDidMount = this.onEditorDidMount.bind(this);
        this.onEditorChange = this.onEditorChange.bind(this);
        this.onTabClicked = this.onTabClicked.bind(this);
        this.onTabCloseClicked = this.onTabCloseClicked.bind(this);
    }
    componentWillMount() {
        const currentEditor = this.props.store.sidebar.editorWorkspaces.current;
        if (currentEditor) {
            currentEditor.files.forEach((file, uri) => {
                monaco.editor.createModel(file.content, file.language, monaco.Uri.parse(uri));
            });
        }
    }
    onEditorDidMount(e) {
        this._editor = e;
        this.props.editorDidMount(e);
        this._updateModelDisposer = autorun(() => {
            // automatically run when we change model
            const currentEditor = this.props.store.sidebar.editorWorkspaces.current;
            if (currentEditor) {
                // set new model in editor
                e.setModel(currentEditor.selected ? monaco.editor.getModel(monaco.Uri.parse(currentEditor.selected.uri)) : null);
                if (currentEditor.selected && currentEditor.selected.state) {
                    // restore state if any
                    e.restoreViewState(currentEditor.selected.state);
                }
            }
        });
        this._lintModelWhenExecuted = autorun(() => {
            // automatically run when we change model
            const { data, sidebar } = this.props.store;
            const currentEditor = sidebar.editorWorkspaces.current;
            if (currentEditor) {
                if (currentEditor.selected && currentEditor.selected.uri === EditorStore.MAIN) {
                    if (data) {
                        // this is just a trick to trigger autorun when "data" changes
                        Object.keys(data);
                    }
                    gcLangService.triggerChange();
                }
            }
        });
        this._editor.addAction({
            id: 'execute-greycat-main',
            label: 'Execute main.gcl',
            keybindings: [
                monaco.KeyMod.Shift | monaco.KeyCode.Enter,
            ],
            contextMenuGroupId: 'navigation',
            contextMenuOrder: 0,
            run: () => {
                this.props.store.execute();
            },
        });
    }
    onEditorChange(newValue) {
        if (this._editor) {
            const model = this._editor.getModel();
            const { store } = this.props;
            if (store.sidebar.editorWorkspaces.current) {
                store.sidebar.editorWorkspaces.current.setContent(model.uri.path, newValue);
            }
            if (model.uri.path === EditorStore.EXTENSIONS) {
                if (this._interpretTimeout) {
                    clearTimeout(this._interpretTimeout);
                }
                this._interpretTimeout = setTimeout(() => {
                    store.interpretExtensions();
                }, 500);
            }
        }
    }
    onTabClicked(uri) {
        return () => {
            const { sidebar } = this.props.store;
            const editor = sidebar.editorWorkspaces.current;
            if (editor) {
                if (this._editor && editor.selected) {
                    editor.selected.state = this._editor.saveViewState();
                }
                editor.select(uri);
            }
        };
    }
    onTabCloseClicked(uri) {
        return (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.props.store.sidebar.editorWorkspaces.current) {
                this.props.store.sidebar.editorWorkspaces.current.close(uri);
            }
        };
    }
    componentWillUnmount() {
        if (this._updateModelDisposer) {
            this._updateModelDisposer();
        }
        if (this._lintModelWhenExecuted) {
            this._lintModelWhenExecuted();
        }
    }
    render() {
        const { store } = this.props;
        const editor = store.sidebar.editorWorkspaces.current;
        if (editor) {
            const { name, opened, selected } = editor;
            const modelUri = monaco.Uri.parse(selected.uri);
            let model = monaco.editor.getModel(modelUri);
            if (model) {
                if (model.getValue() !== selected.content) {
                    model.setValue(selected.content);
                }
            }
            else {
                model = monaco.editor.createModel(selected.content, selected.language, modelUri);
            }
            return (React.createElement(Pane, { className: styles.root, panel: "editor", title: "Editor", subTitle: name, actions: [{ className: cx(styles.run, 'fas', 'fa-play'), onClick: store.execute, title: 'Run [Shift + Enter]' }], store: store.layout },
                React.createElement("div", { className: styles.header },
                    React.createElement("div", { className: styles.fileTabs }, opened.map(({ uri, language, isSelected }) => (React.createElement("div", { className: cx(styles.tab, { [styles.selected]: isSelected }), key: uri, onClick: this.onTabClicked(uri) },
                        React.createElement("div", { className: "flex" },
                            React.createElement(LanguageIcon, { language: language }),
                            React.createElement("span", { className: styles.name }, uri)))))),
                    React.createElement("div", { className: styles.actions })),
                React.createElement("div", { className: styles.monacoContainer },
                    React.createElement(MonacoEditor, { theme: store.settings.nightMode ? 'vs-dark' : 'vs', height: "100%", options: {
                            model,
                            folding: true,
                            extraEditorClassName: styles.monacoGreycat,
                            scrollBeyondLastLine: false,
                            minimap: {
                                enabled: false,
                            },
                            renderWhitespace: 'boundary',
                            autoIndent: true,
                        }, editorDidMount: this.onEditorDidMount, onChange: this.onEditorChange }))));
        }
        else {
            return (React.createElement(Pane, { className: styles.root, panel: "editor", title: "Editor", store: store.layout },
                React.createElement("div", { className: styles.noWorkspace },
                    React.createElement("div", { className: styles.center },
                        React.createElement("h2", { className: "muted" }, "No workspace found"),
                        React.createElement("p", { className: "muted" },
                            "Click ",
                            React.createElement("code", null, "New workspace"),
                            " in ",
                            React.createElement("code", null, "Editor Workspaces"),
                            " in the left panel, and press ",
                            React.createElement("code", null, "Enter"))))));
        }
    }
};
EditorPane = __decorate([
    observer,
    __metadata("design:paramtypes", [Object])
], EditorPane);
export default EditorPane;
